import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { KeyResult } from '@momentum/types';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/Popover';
import { useUpdateGoal } from '@/shared/hooks/useGoals';

interface KPIProgressUpdateProps {
  goalId: string;
  keyResult: KeyResult;
  keyResultIndex: number;
  allKeyResults: KeyResult[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export const KPIProgressUpdate: React.FC<KPIProgressUpdateProps> = ({
  goalId,
  keyResult,
  keyResultIndex,
  allKeyResults,
  isOpen,
  onOpenChange,
  children,
}) => {
  const { t } = useTranslation('goals');
  const [progressValue, setProgressValue] = useState<string>('');
  const updateGoalMutation = useUpdateGoal();

  useEffect(() => {
    if (isOpen) {
      setProgressValue(keyResult.currentProgress?.toString());
    } else {
      setProgressValue('');
    }
  }, [isOpen, keyResult.currentProgress]);

  const handleUpdate = async () => {
    const newProgress = parseFloat(progressValue);

    if (isNaN(newProgress)) {
      toast.error(t('kpi.invalidValue'), {
        description: t('kpi.invalidValueDescription'),
      });

      return;
    }

    if (newProgress < 0) {
      toast.error(t('kpi.invalidValue'), {
        description: t('kpi.cannotBeNegative'),
      });

      return;
    }

    const cappedProgress = Math.min(newProgress, keyResult.quantity);

    const updatedKeyResults = allKeyResults.map((kr, i) =>
      i === keyResultIndex ? { ...kr, currentProgress: cappedProgress } : kr
    );

    try {
      await updateGoalMutation.mutateAsync({
        id: goalId,
        data: { keyResults: updatedKeyResults },
      });

      toast.success(t('kpi.progressUpdated'), {
        description: t('kpi.progressUpdatedDescription')(
          keyResult.title,
          cappedProgress,
          keyResult.unit
        ),
      });

      // Close popover
      onOpenChange(false);
    } catch {
      toast.error(t('kpi.updateFailed'), {
        description: t('kpi.updateFailedDescription'),
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUpdate();
    }
  };

  const isUpdating = updateGoalMutation.isPending && isOpen;

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-64" align="end">
        <div className="space-y-3">
          <div className="space-y-1">
            <h4 className="text-sm font-medium">{t('kpi.updateProgress')}</h4>
            <p className="text-xs text-muted-foreground">
              {t('kpi.setProgressFor')(keyResult.title)}
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="0"
                value={progressValue}
                onChange={(e) => setProgressValue(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-25"
                min="0"
                max={keyResult.quantity}
                autoFocus
              />
              <span className="text-sm text-muted-foreground">
                {keyResult.unit}
              </span>
            </div>
            <div className="text-xs text-muted-foreground">
              {t('kpi.maxValue')}: {keyResult.quantity} {keyResult.unit}
            </div>
            <Button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="w-full"
              size="sm"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="me-2 h-3 w-3 animate-spin" />
                  {t('kpi.updating')}
                </>
              ) : (
                t('kpi.updateProgressButton')
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
